import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MovieCard from '../components/MovieCard';
import './HomePage.css';
import './KioskDisplayPage.css';

const POLL_INTERVAL = 2500;

function statusLabel(status) {
  if (status === 'active') return 'On screen';
  if (status === 'pending') return 'Waiting';
  return 'Shown';
}

function KioskResultsPage() {
  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [currentRes, queueRes] = await Promise.all([
        axios.get('/api/kiosk/current'),
        axios.get('/api/kiosk/queue'),
      ]);
      setCurrent(currentRes.data);
      setQueue(queueRes.data || []);
    } catch (error) {
      console.error('Error fetching kiosk results:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [refresh]);

  const runAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (error) {
      console.error('Kiosk panel action failed:', error);
    } finally {
      setBusy(false);
    }
  };

  const handleAdvance = () => runAction(() => axios.post('/api/kiosk/advance'));
  const handleShow = (id) => runAction(() => axios.post(`/api/kiosk/show/${id}`));
  const handleClear = () => {
    if (!window.confirm('Clear every visitor from the queue? This cannot be undone.')) return;
    runAction(() => axios.post('/api/kiosk/clear'));
  };

  const waiting = queue.filter((item) => item.status === 'pending');
  const done = queue.filter((item) => item.status === 'shown').slice().reverse();
  const live = queue.find((item) => item.status === 'active');
  const waitingCount = waiting.length + (live ? 1 : 0);

  if (loading) {
    return <div className="kiosk-page kiosk-idle"><p className="page-loading">Loading...</p></div>;
  }

  return (
    <div className={`kiosk-page kiosk-active ${panelOpen ? 'panel-open' : ''}`}>
      {current ? (
        <>
          <div className="kiosk-active-header">
            <p className="kiosk-eyebrow">Now searching for</p>
            <h1 className="kiosk-nickname">{current.nickname}</h1>
            {current.queue_length > 0 && (
              <p className="kiosk-queue-note">{current.queue_length} more waiting</p>
            )}
          </div>
          <div className="kiosk-results">
            <div className="movie-grid wrap">
              {current.movies.map((movie) => (
                <MovieCard key={movie.movieid} movie={movie} />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="kiosk-results-idle">
          <p className="kiosk-eyebrow">Results screen</p>
          <h1 className="kiosk-nickname">Waiting for a visitor</h1>
          <p className="kiosk-queue-note">QR stays on the other screen — people can keep joining the line.</p>
        </div>
      )}

      <button
        className={`kiosk-panel-toggle ${panelOpen ? 'open' : ''}`}
        onClick={() => setPanelOpen((open) => !open)}
        type="button"
        aria-expanded={panelOpen}
      >
        {panelOpen ? '›' : 'Panel'}
        {!panelOpen && waitingCount > 0 && <span className="kiosk-panel-badge">{waitingCount}</span>}
      </button>
      <aside className={`kiosk-panel ${panelOpen ? 'open' : ''}`}>
        <div className="kiosk-panel-head">
          <h2>Queue desk</h2>
          <p>Staff controls for QR visitors</p>
        </div>

        <div className="kiosk-panel-actions">
          <button type="button" className="kiosk-panel-primary" onClick={handleAdvance} disabled={busy || !live}>
            {busy ? 'Working…' : 'Next visitor →'}
          </button>
          <button type="button" className="kiosk-panel-danger" onClick={handleClear} disabled={busy || queue.length === 0}>
            Clear all
          </button>
        </div>

        <section className="kiosk-panel-section">
          <h3>On screen</h3>
          {live ? (
            <div className="kiosk-panel-row current">
              <div>
                <strong>{live.nickname}</strong>
                <span>{live.movie_count} matches</span>
              </div>
            </div>
          ) : (
            <p className="kiosk-panel-empty">Nobody on screen</p>
          )}
        </section>

        <section className="kiosk-panel-section">
          <h3>Waiting ({waiting.length})</h3>
          {waiting.length === 0 ? (
            <p className="kiosk-panel-empty">Nobody in line</p>
          ) : (
            waiting.map((item, index) => (
              <div className="kiosk-panel-row" key={item.id}>
                <div>
                  <strong>{index + 1}. {item.nickname}</strong>
                  <span>{item.movie_count} matches</span>
                </div>
                <button type="button" onClick={() => handleShow(item.id)} disabled={busy}>
                  Show
                </button>
              </div>
            ))
          )}
        </section>

        <section className="kiosk-panel-section">
          <h3>Shown ({done.length})</h3>
          {done.length === 0 ? (
            <p className="kiosk-panel-empty">None yet</p>
          ) : (
            done.map((item) => (
              <div className="kiosk-panel-row" key={item.id}>
                <div>
                  <strong>{item.nickname}</strong>
                  <span>{statusLabel(item.status)}</span>
                </div>
                <button type="button" onClick={() => handleShow(item.id)} disabled={busy}>
                  Show again
                </button>
              </div>
            ))
          )}
        </section>
      </aside>
    </div>
  );
}

export default KioskResultsPage;
